import FuzzySet from "fuzzyset";

export interface ExpectedFileMatch<T> {
  id: T;
  baseName: string;
  keywords: string[];
  allowedExtensions: string[];
}

const DEFAULT_THRESHOLD = 0.4;

/**
 * Uses fuzzy matching and keyword matching to find the best match for an uploaded file.
 * File extensions must match exactly. Base names are matched using:
 * 1. Fuzzy string matching against expected filenames
 * 2. Keyword matching - checking if identifying terms appear in the filename
 *
 * @param uploadedFileName - The name of the file uploaded by the user
 * @param expected - An array of expected file configurations to match against
 * @param threshold - Minimum score threshold for a match (0-1), default is 0.4
 * @returns The matched file configuration or null if no match found
 *
 * @example
 * fuzzyMatchFile("generic_customer_broadband_BOM_v1.0.csv", expected) // Returns { id: "bom", score: 0.9 }
 * fuzzyMatchFile("layers-configuration.json", expected) // Returns { id: "layersConfiguration", score: ... }
 * fuzzyMatchFile("cost_breakdown_2024.csv", expected) // Returns { id: "costBreakdown", score: ... }
 */
export const fuzzyMatchFile = <T>(
  uploadedFileName: string,
  expected: ExpectedFileMatch<T>[],
  threshold: number = DEFAULT_THRESHOLD
): { id: T; score: number } | null => {
  const lastDotIndex = uploadedFileName.lastIndexOf(".");

  if (lastDotIndex === -1) return null;

  const uploadedExtension = uploadedFileName.slice(lastDotIndex).toLowerCase();
  const uploadedBaseName = uploadedFileName.slice(0, lastDotIndex).toLowerCase();

  // Filter by extension first (must match, case-insensitive)
  const candidates = expected.filter((f) => f.allowedExtensions.some((ext) => ext.toLowerCase() === uploadedExtension));

  if (candidates.length === 0) return null;

  let bestMatch: ExpectedFileMatch<T> | null = null;
  let bestScore = 0;

  for (const candidate of candidates) {
    // Strategy 1: Fuzzy match against the full expected base name
    const fuzzySet = FuzzySet([candidate.baseName.toLowerCase()]);
    const fuzzyMatches = fuzzySet.get(uploadedBaseName);
    const fuzzyScore = fuzzyMatches?.[0]?.[0] || 0;

    // Strategy 2: Check if any keywords appear in the uploaded filename
    // This helps match files like "generic_customer_name_broadband_BOM_v1.0.csv" to "bom"
    let keywordScore = 0;
    for (const keyword of candidate.keywords) {
      const lowered = keyword.toLowerCase();
      if (uploadedBaseName.includes(lowered)) {
        // Give higher score if keyword appears as a separate word/segment
        const isWordBoundary = new RegExp(`(^|[_\\-\\s])${keyword}([_\\-\\s]|$)`, "i").test(uploadedBaseName) || uploadedBaseName === lowered;

        keywordScore = Math.max(keywordScore, isWordBoundary ? 0.9 : 0.7);
      }
    }

    // Use the better of fuzzy or keyword score
    const score = Math.max(fuzzyScore, keywordScore);

    if (score > bestScore) {
      bestScore = score;
      bestMatch = candidate;
    }
  }

  // Accept if score is above threshold (0.4 = 40% similarity)
  if (bestScore < threshold || !bestMatch) return null;

  return { id: bestMatch.id, score: bestScore };
};
